import { useEffect, useMemo, useState } from "react";
import client from "../api/client";

const companyId = () => Number(localStorage.getItem("companyId") || 0);
const today = () => new Date().toISOString().split("T")[0];

const val = (r, ...keys) =>
  keys.map((k) => r?.[k]).find((x) => x !== undefined && x !== null) ?? "";

const safe = (v) =>
  v === null || v === undefined ? "" : typeof v === "object" ? "" : String(v);

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const money = (v) =>
  num(v).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const getArray = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.rows)) return data.rows;
  if (Array.isArray(data?.result)) return data.result;
  return [];
};

export default function PurchaseOrderPage() {
  const [suppliers, setSuppliers] = useState([]);
  const [items, setItems] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loadingMasters, setLoadingMasters] = useState(false);
  const [rows, setRows] = useState([]);

  const [form, setForm] = useState({
    orderNo: "",
    orderDate: today(),
    supplierId: "",
    deliveryDate: "",
    notes: "",
  });

  const [line, setLine] = useState({
    itemId: "",
    qty: 1,
    rate: 0,
    gstPercent: 18,
    remarks: "",
  });

  const subtotal = useMemo(
    () => rows.reduce((s, x) => s + num(x.lineTotal), 0),
    [rows]
  );

  const taxAmount = useMemo(
    () => rows.reduce((s, x) => s + num(x.taxAmount), 0),
    [rows]
  );

  const grandTotal = subtotal + taxAmount;

  useEffect(() => {
    loadMasters();
    loadNextNo();
  }, []);

  const loadMasters = async () => {
    try {
      setLoadingMasters(true);

      const [s, i] = await Promise.all([
        client.get("/suppliers", {
          params: { companyId: companyId() || undefined },
        }),
        client.get("/inventory/items", {
          params: { companyId: companyId() || undefined },
        }),
      ]);

      setSuppliers(getArray(s.data));
      setItems(getArray(i.data));
    } catch (e) {
      const msg =
        e?.response?.data?.message ||
        e?.response?.data?.title ||
        e?.message ||
        "Failed to load masters";
      alert(msg);
      setSuppliers([]);
      setItems([]);
    } finally {
      setLoadingMasters(false);
    }
  };

  const loadNextNo = async () => {
    try {
      const res = await client.get("/purchase-orders/next-no", {
        params: { companyId: companyId() || undefined },
      });

      setForm((x) => ({
        ...x,
        orderNo:
          safe(val(res.data, "OrderNo", "orderNo", "VoucherNo", "voucherNo")) ||
          x.orderNo,
      }));
    } catch {
      // Do not show empty alert. User can manually enter PO No.
    }
  };

  const handleItemChange = (itemId) => {
    const item = items.find((x) => String(val(x, "Id", "id")) === String(itemId));

    setLine((x) => ({
      ...x,
      itemId,
      rate: num(
        val(
          item,
          "PurchaseRate",
          "purchaseRate",
          "CostPrice",
          "costPrice",
          "Rate",
          "rate"
        )
      ),
      gstPercent:
        num(val(item, "GSTPercent", "gstPercent", "GstPercent", "gst")) ||
        x.gstPercent ||
        18,
    }));
  };

  const addItem = () => {
    if (!line.itemId) return alert("Select item");
    if (num(line.qty) <= 0) return alert("Qty required");
    if (num(line.rate) < 0) return alert("Invalid rate");

    const item = items.find((x) => String(val(x, "Id", "id")) === String(line.itemId));

    const itemName = safe(val(item, "ItemName", "itemName", "Name", "name"));

    if (!itemName) return alert("Selected item not found");

    const qty = num(line.qty);
    const rate = num(line.rate);
    const gstPercent = num(line.gstPercent);

    const lineTotal = qty * rate;
    const tax = (lineTotal * gstPercent) / 100;

    setRows((prev) => [
      ...prev,
      {
        itemId: num(line.itemId),
        itemName,
        qty,
        rate,
        gstPercent,
        remarks: safe(line.remarks),
        lineTotal,
        taxAmount: tax,
        totalAmount: lineTotal + tax,
      },
    ]);

    setLine({
      itemId: "",
      qty: 1,
      rate: 0,
      gstPercent: 18,
      remarks: "",
    });
  };

  const removeItem = (index) => {
    setRows((prev) => prev.filter((_, x) => x !== index));
  };

  const save = async () => {
    if (!form.orderNo.trim()) return alert("PO No required");
    if (!form.orderDate) return alert("PO Date required");
    if (!form.supplierId) return alert("Supplier required");
    if (rows.length === 0) return alert("Items required");

    try {
      setSaving(true);

      await client.post("/purchase-orders", {
        companyId: companyId(),
        orderNo: form.orderNo.trim(),
        orderDate: form.orderDate,
        supplierId: num(form.supplierId),
        deliveryDate: form.deliveryDate || null,
        subTotal: subtotal,
        cgstAmount: taxAmount / 2,
        sgstAmount: taxAmount / 2,
        igstAmount: 0,
        taxAmount,
        discountAmount: 0,
        grandTotal,
        notes: safe(form.notes),
        details: rows,
      });

      alert("Purchase order saved");

      setRows([]);
      setForm({
        orderNo: "",
        orderDate: today(),
        supplierId: "",
        deliveryDate: "",
        notes: "",
      });

      await loadNextNo();
    } catch (e) {
      const msg =
        e?.response?.data?.message ||
        e?.response?.data?.title ||
        e?.message ||
        "Failed to save purchase order";
      alert(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="container-fluid py-2" style={{ fontSize: 11, padding: 0 }}>
      <div
        style={{
          background: "#fff",
          borderRadius: 14,
          overflow: "hidden",
          boxShadow: "0 2px 10px rgba(0,0,0,.08)",
        }}
      >
        <div
          style={{
            background:
              "linear-gradient(90deg, rgb(11, 79, 74), rgb(15, 118, 110))",
            color: "#fff",
            padding: "9px 12px",
          }}
        >
          <h6 style={{ fontSize: 13, fontWeight: 800, margin: 0 }}>
            <i className="bi bi-cart-check me-2"></i>
            Purchase Order
          </h6>

          <div style={{ fontSize: 10, opacity: 0.85 }}>
            Procurement workflow before GRN and Purchase Voucher
          </div>
        </div>

        <div style={{ padding: 10 }}>
          <div className="row g-2 mb-2">
            <Field label="PO No">
              <input
                className="form-control form-control-sm"
                value={form.orderNo}
                onChange={(e) => setForm({ ...form, orderNo: e.target.value })}
              />
            </Field>

            <Field label="PO Date">
              <input
                type="date"
                className="form-control form-control-sm"
                value={form.orderDate}
                onChange={(e) => setForm({ ...form, orderDate: e.target.value })}
              />
            </Field>

            <Field label="Supplier">
              <select
                className="form-select form-select-sm"
                value={form.supplierId}
                onChange={(e) => setForm({ ...form, supplierId: e.target.value })}
                disabled={loadingMasters}
              >
                <option value="">
                  {loadingMasters ? "Loading..." : "Select Supplier"}
                </option>

                {suppliers.map((s, i) => (
                  <option key={val(s, "Id", "id") || i} value={val(s, "Id", "id")}>
                    {safe(val(s, "SupplierName", "supplierName", "Name", "name"))}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="Delivery Date">
              <input
                type="date"
                className="form-control form-control-sm"
                value={form.deliveryDate}
                onChange={(e) =>
                  setForm({ ...form, deliveryDate: e.target.value })
                }
              />
            </Field>

            <Field label="Notes" col="col-md-4">
              <input
                className="form-control form-control-sm"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
              />
            </Field>
          </div>

          <div
            className="row g-2 mb-2 p-2"
            style={{
              background: "#f8fafc",
              border: "1px solid #e9ecef",
              borderRadius: 10,
            }}
          >
            <Field label="Item" col="col-md-3">
              <select
                className="form-select form-select-sm"
                value={line.itemId}
                onChange={(e) => handleItemChange(e.target.value)}
                disabled={loadingMasters}
              >
                <option value="">
                  {loadingMasters ? "Loading..." : "Select Item"}
                </option>

                {items.map((i, index) => (
                  <option key={val(i, "Id", "id") || index} value={val(i, "Id", "id")}>
                    {safe(val(i, "ItemName", "itemName", "Name", "name"))}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="Qty">
              <input
                type="number"
                min="0"
                className="form-control form-control-sm"
                value={line.qty}
                onChange={(e) => setLine({ ...line, qty: e.target.value })}
              />
            </Field>

            <Field label="Rate">
              <input
                type="number"
                min="0"
                className="form-control form-control-sm"
                value={line.rate}
                onChange={(e) => setLine({ ...line, rate: e.target.value })}
              />
            </Field>

            <Field label="GST %">
              <input
                type="number"
                min="0"
                className="form-control form-control-sm"
                value={line.gstPercent}
                onChange={(e) =>
                  setLine({ ...line, gstPercent: e.target.value })
                }
              />
            </Field>

            <Field label="Remarks" col="col-md-3">
              <input
                className="form-control form-control-sm"
                value={line.remarks}
                onChange={(e) => setLine({ ...line, remarks: e.target.value })}
              />
            </Field>

            <div className="col-md-1 d-flex align-items-end">
              <button
                className="btn btn-primary btn-sm w-100"
                onClick={addItem}
                disabled={loadingMasters}
              >
                Add
              </button>
            </div>
          </div>

          <div
            style={{
              maxHeight: 350,
              overflow: "auto",
              border: "1px solid #e9ecef",
              borderRadius: 10,
            }}
          >
            <table
              className="table table-bordered table-hover align-middle"
              style={{ fontSize: 11, marginBottom: 0, minWidth: 900 }}
            >
              <thead>
                <tr>
                  {[
                    "Item",
                    "Qty",
                    "Rate",
                    "GST %",
                    "Tax",
                    "Line Total",
                    "Grand",
                    "Action",
                  ].map((h) => (
                    <TH key={h}>{h}</TH>
                  ))}
                </tr>
              </thead>

              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td
                      colSpan="8"
                      className="text-center text-muted"
                      style={{ padding: 18 }}
                    >
                      No items added
                    </td>
                  </tr>
                ) : (
                  rows.map((r, i) => (
                    <tr key={`${r.itemId}-${i}`}>
                      <td>{safe(r.itemName)}</td>
                      <td className="text-end">{money(r.qty)}</td>
                      <td className="text-end">{money(r.rate)}</td>
                      <td className="text-end">{money(r.gstPercent)}</td>
                      <td className="text-end">{money(r.taxAmount)}</td>
                      <td className="text-end">{money(r.lineTotal)}</td>
                      <td className="text-end fw-bold">{money(r.totalAmount)}</td>
                      <td>
                        <button
                          className="btn btn-outline-danger btn-sm"
                          onClick={() => removeItem(i)}
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>

              {rows.length > 0 && (
                <tfoot>
                  <tr style={{ background: "#f8fafc", fontWeight: 800 }}>
                    <td className="text-end" colSpan="4">
                      Total
                    </td>
                    <td className="text-end">{money(taxAmount)}</td>
                    <td className="text-end">{money(subtotal)}</td>
                    <td className="text-end">{money(grandTotal)}</td>
                    <td></td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>

          <div className="row g-2 mt-2">
            <Stat title="Sub Total" value={money(subtotal)} />
            <Stat title="Tax" value={money(taxAmount)} />
            <Stat title="Grand Total" value={money(grandTotal)} />
          </div>

          <div className="d-flex justify-content-end mt-3">
            <button
              className="btn btn-success btn-sm"
              disabled={saving}
              onClick={save}
            >
              {saving ? "Saving..." : "Save Purchase Order"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children, col = "col-md-2" }) {
  return (
    <div className={col}>
      <label style={{ fontSize: 10, fontWeight: 700, marginBottom: 3 }}>
        {label}
      </label>
      {children}
    </div>
  );
}

function Stat({ title, value }) {
  return (
    <div className="col-md-4">
      <div
        style={{
          border: "1px solid #e9ecef",
          background: "#f8fafc",
          borderRadius: 10,
          padding: 9,
          borderTop: "3px solid #0f766e",
        }}
      >
        <div style={{ fontSize: 10, color: "#64748b", fontWeight: 800 }}>
          {title}
        </div>

        <div style={{ fontSize: 15, color: "#0f766e", fontWeight: 900 }}>
          ₹ {value}
        </div>
      </div>
    </div>
  );
}

function TH({ children }) {
  return (
    <th
      style={{
        fontSize: 10,
        color: "#fff",
        background: "#07111f",
        padding: "7px 8px",
        whiteSpace: "nowrap",
        position: "sticky",
        top: 0,
        zIndex: 2,
      }}
    >
      {children}
    </th>
  );
}